import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useDeactivateCourt } from '../../../../hooks/court/useDeactivateCourt'

import { CourtCard } from './courtCard/CourtCard'
import { ListingDetails } from '../../../listing/ListingDetails'

import { CONFIG } from '../../../../config'
import { useApp } from '../../../../contexts/AppContext'
import { useConfirm } from '../../../../contexts/ConfirmContext'
import { ConfirmDetailType, ConfirmLayout } from '../../../../types/confirm/confirm'
import { DisplayType } from '../../../../types/listing'
import { CourtModelMetadata } from '../../../../types/court/court'
import { BannerKind } from '../../../../types/contexts/AppContext'
import { ActionType } from '../../../../constants/actionTypes'

type CourtListProps = {
  clubId: string | undefined
}

export const CourtList = ({
  clubId,
}: CourtListProps) => {
  const navigate = useNavigate()
  const { showBanner } = useApp()
  const { confirm } = useConfirm()
  const { t } = useTranslation('courts')

  const { deactivate } = useDeactivateCourt()
  const [refreshTrigger, setRefreshTrigger] = useState(false)

  const handleDelete = (court: CourtModelMetadata) => {
    const courtId = court.id

    if (courtId === undefined) {
      return
    }

    const returnTo = clubId
      ? `/m/courts/club/${clubId}`
      : '/m/courts'

    confirm({
      layout: ConfirmLayout.DETAILS,
      title: t('courts.delete-confirmation.title'),
      subtitle: t('courts.delete-confirmation.subtitle'),
      confirmLabel: t('courts.delete-confirmation.confirm'),
      backLabel: t('courts.delete-confirmation.cancel'),
      returnTo,
      itemCards: [
        {
          id: courtId,
          title: court.name,
          details: [
            {
              label: t('courts.card.configuration'),
              value: court.clubName,
            },
            {
              label: t('courts.edit.fields.type'),
              value: t(`courts.type.${court.type}`),
            },
            {
              key: 'futureBookings',
              label: t('courts.delete-confirmation.future-bookings'),
              value: 'KEEP',
              type: ConfirmDetailType.SELECT,
              options: [
                { value: 'KEEP', label: t('courts.delete-confirmation.keep') },
                { value: 'CANCEL', label: t('courts.delete-confirmation.cancel-bookings') },
              ],
            },
          ],
        },
      ],
      warning: t('courts.delete-confirmation.warning'),
      onConfirm: async (_, selectedValues) => {
        await deactivate(courtId, selectedValues?.futureBookings === 'CANCEL' ? 'CANCEL' : 'KEEP')
        setRefreshTrigger(value => !value)

        showBanner({
          kind: BannerKind.OK,
          msg: t('courts.court.successfully-deleted'),
        })
      },
    })
  }

  const handleAction = (court: CourtModelMetadata, action: ActionType) => {
    if (court.id === undefined) {
      return
    }

    switch (action) {
      case ActionType.EDIT:
        navigate(`/m/courts/${court.id}`)
        return
      case ActionType.BLOCK:
        navigate(`/m/blocks`)
        return
      case ActionType.DELETE:
        handleDelete(court)
        return
    }
  }

  const renderCourt = (court: CourtModelMetadata) => {
    return (
      <CourtCard court={court} onAction={handleAction} />
    )
  }

  return (
    <ListingDetails<CourtModelMetadata>
      endpoint={`${CONFIG.BOUNCE_API}/api/v1/management/courts`}
      initialViewMode={DisplayType.GRID}
      renderItem={renderCourt}
      additionalParams={clubId ? { clubId } : undefined}
      refreshTrigger={refreshTrigger}
      gridItemClassName="col-12 col-xxl-6"
    />
  )
}
